import React, { useState, useEffect } from 'react';
import zhCN from '../../locale/zh_CN';
import enUS from '../../locale/en_US';
import viVN from '../../locale/vi_VN';
import itIT from '../../locale/it_IT';
import jaJP from '../../locale/ja_JP';
import esES from '../../locale/es_ES';
import ruRU from '../../locale/ru_RU';
import msMY from '../../locale/ms_MY';
import zhTW from '../../locale/zh_TW';
import laLA from '../../locale/la_LA';
import { getLang } from '../util';

export interface IntlType {
  locale: string;
  getMessage: (id: string, defaultMessage: string) => string;
}

// eslint-disable-next-line @typescript-eslint/ban-types
function get(
  source: object,
  path: string,
  defaultValue?: string
): string | undefined {
  // a[3].b -> a.3.b
  const paths = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let result = source;
  let message = defaultValue;
  // eslint-disable-next-line no-restricted-syntax
  for (const p of paths) {
    message = Object(result)[p];
    result = Object(result)[p];
    if (message === undefined) {
      return defaultValue;
    }
  }
  return message;
}

/**
 * 创建一个操作函数
 * @param locale
 * @param localeMap
 */
const createIntl = (
  locale: string,
  localeMap: { [key: string]: any }
): IntlType => ({
  getMessage: (id: string, defaultMessage: string) =>
    get(localeMap, id, defaultMessage) || defaultMessage,
  locale,
});

const zhCNIntl = createIntl('zh_CN', zhCN);
const enUSIntl = createIntl('en_US', enUS);
const viVNIntl = createIntl('vi_VN', viVN);
const itITIntl = createIntl('it_IT', itIT);
const jaJPIntl = createIntl('ja_JP', jaJP);
const esESIntl = createIntl('es_ES', esES);
const ruRUIntl = createIntl('ru_RU', ruRU);
const msMYIntl = createIntl('ms_MY', msMY);
const zhTWIntl = createIntl('zh_TW', zhTW);
const laLAIntl = createIntl('la_LA', laLA);

const intlMap: Record<string, any> = {
  'vi-VN': viVNIntl,
  'en-US': enUSIntl,
  'zh-CN': zhCNIntl,
  'it-IT': itITIntl,
  'js-JP': jaJPIntl,
  'es-ES': esESIntl,
  'ru-RU': ruRUIntl,
  'ms-MY': msMYIntl,
  'zh-TW': zhTWIntl,
  'la-LA': laLAIntl,
};

export {
  enUSIntl,
  zhCNIntl,
  viVNIntl,
  itITIntl,
  jaJPIntl,
  esESIntl,
  ruRUIntl,
  msMYIntl,
  zhTWIntl,
  laLAIntl,
};

// Helper function to get current intl value dynamically
const getCurrentIntl = (): IntlType => {
  const lang = getLang();
  return intlMap[lang || ''] || viVNIntl;
};

const IntlContext = React.createContext<IntlType>(getCurrentIntl());

const { Consumer: IntlConsumer } = IntlContext;

// Custom Provider that reads locale dynamically
// Always reads from localStorage to ensure it works after login
const IntlProvider: React.FC<{
  value?: IntlType;
  children: React.ReactNode;
}> = ({ children }) => {
  // Use state to track locale changes and trigger re-renders
  const [currentValue, setCurrentValue] = useState<IntlType>(getCurrentIntl());

  // Update when localStorage changes (e.g., after login)
  useEffect(() => {
    const updateLocale = () => {
      const newValue = getCurrentIntl();
      setCurrentValue((prevValue) => {
        // Only update if locale actually changed
        if (prevValue.locale !== newValue.locale) {
          return newValue;
        }
        return prevValue;
      });
    };

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener('storage', updateLocale);

    // Also check on mount (handles login redirect)
    updateLocale();

    // Poll for changes (as a fallback for same-tab localStorage changes)
    // This ensures it picks up locale changes even if storage event doesn't fire
    // Poll less frequently to reduce performance impact
    const interval = setInterval(updateLocale, 500);

    return () => {
      window.removeEventListener('storage', updateLocale);
      clearInterval(interval);
    };
  }, []); // Empty deps - only run once on mount

  return (
    <IntlContext.Provider value={currentValue}>{children}</IntlContext.Provider>
  );
};

export { IntlConsumer, IntlProvider, createIntl };

export function useIntl(): IntlType {
  // Always read dynamically to ensure we get the latest locale from localStorage
  // This ensures it works after login when locale is set
  return getCurrentIntl();
}

export default IntlContext;
